import { createContext, type ReactNode, useContext, useMemo } from 'react';
import useUser from '@hooks/useUser';
import { type ChildrenProps } from '@models/ChildrenProps';
import { ProjectRolesHelper } from '@pages/project/settings/_utils/hooks/MembersAttributes/projectRoles';
import { useProjectParticipants } from '@pages/project/settings/_utils/hooks/MembersAttributes/useProjectParticipants';
import {
    type CanFn,
    type GrantedPermission,
    makeCan,
    resolvePermissions,
} from '../access/project-permissions';

interface ProjectAccessContextProps {
    can: CanFn;
    permissions: GrantedPermission[];
    roleHelper: ProjectRolesHelper;
    loading: boolean;
}

/**
 * Default outside a project (e.g. standards/admin pages that reuse shared
 * components): allow everything so nothing is disabled by accident.
 */
const ALLOW_ALL: GrantedPermission[] = ['*:*'];

const ProjectAccessContext = createContext<ProjectAccessContextProps>({
    can: makeCan(ALLOW_ALL),
    permissions: ALLOW_ALL,
    roleHelper: new ProjectRolesHelper([]),
    loading: false,
});

export function useProjectAccess(): ProjectAccessContextProps {
    return useContext(ProjectAccessContext);
}

export function ProjectAccessProvider({ children }: ChildrenProps): ReactNode {
    const { options: participants, loading } = useProjectParticipants();
    const user = useUser();

    const roleHelper = useMemo(
        () => new ProjectRolesHelper(participants),
        [participants],
    );

    const permissions = useMemo(() => {
        const roleId = user ? roleHelper.getRole(user.id)?.id : undefined;
        return resolvePermissions(roleId);
    }, [roleHelper, user]);

    const value = useMemo<ProjectAccessContextProps>(
        () => ({
            can: makeCan(permissions),
            permissions,
            roleHelper,
            loading,
        }),
        [permissions, roleHelper, loading],
    );

    return (
        <ProjectAccessContext.Provider value={value}>
            {children}
        </ProjectAccessContext.Provider>
    );
}
